import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../markets/api/apiClient';
import type { PiprBundle } from '../pipr/types';
import { loadPiprChart, type ChartRequest } from './piprClient';
import { SchemaError } from './piprWire';

export type ChartError = 'schema' | 'empty' | 'network';

export interface PiprChartState {
    data: PiprBundle | null;
    loading: boolean;
    error: ChartError | null;
}

function errorKind(err: unknown): ChartError {
    if (err instanceof SchemaError) return 'schema';
    if (err instanceof ApiError && err.status === 404) return 'empty';
    return 'network';
}

export function usePiprChart(req: ChartRequest | null): PiprChartState {
    const [state, setState] = useState<PiprChartState>({
        data: null,
        loading: req !== null,
        error: null,
    });
    const bankRef = useRef<string | null>(null);

    const bank = req?.bank ?? null;
    const from = req?.from.getTime() ?? null;
    const to = req?.to.getTime() ?? null;
    const resolution = req?.resolution ?? null;

    useEffect(() => {
        if (
            bank === null ||
            from === null ||
            to === null ||
            resolution === null
        ) {
            return;
        }
        let cancelled = false;
        const sameBank = bankRef.current === bank;
        bankRef.current = bank;
        setState(prev => ({
            data: sameBank ? prev.data : null,
            loading: true,
            error: null,
        }));
        loadPiprChart({
            bank,
            from: new Date(from),
            to: new Date(to),
            resolution,
        })
            .then(data => {
                if (!cancelled) setState({ data, loading: false, error: null });
            })
            .catch(err => {
                if (cancelled) return;
                const kind = errorKind(err);
                setState(prev => ({
                    data: sameBank && kind !== 'empty' ? prev.data : null,
                    loading: false,
                    error: kind,
                }));
            });
        return () => {
            cancelled = true;
        };
    }, [bank, from, to, resolution]);

    return state;
}
